import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { theme } from '@/constants/theme';
import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import {
  buildRuntimePracticePath,
  buildRuntimeReversePath,
  buildRuntimeReverseResultPath,
  buildRuntimeReviewPath,
  resolveRuntimeBase,
} from '@/features/unified-learning/runtime/runtimeRoutes';
import { buildReverseLearningQueue, summarizeReverseSession, type ReverseQueueItem } from '@/features/unified-learning/runtime/reverseLearningService';
import type { KpActionRuntime, KpQualityTier, KpSubmitScenarioActionResponse } from '@/types/kursi-play-v2';

const BG = '#0F0F1A';
const SURFACE = '#1E1B3A';
const BORDER = '#2D2B50';
const TEXT = '#FFFFFF';
const TEXT_MED = '#A0A0B8';
const PRIMARY = theme.colors.primary;

const QUALITY_LABELS: Record<string, string> = {
  strong: 'מעולה',
  acceptable: 'סביר',
  weak: 'חלש',
  risky: 'מסוכן',
};

export function ReverseLearningGapSessionScreen() {
  const { slug, uid, token, target } = useLocalSearchParams<{ slug: string; uid: string; token?: string; target?: string }>();
  const runtimeSession = { uid: String(uid ?? ''), token: typeof token === 'string' ? token : undefined };
  const runtimeBase = resolveRuntimeBase(usePathname());
  const targetQuestions = Number(target ?? 8) || 8;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState<ReverseQueueItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<KpSubmitScenarioActionResponse | null>(null);
  const [sessionXp, setSessionXp] = useState(0);
  const [qualityTiers, setQualityTiers] = useState<KpQualityTier[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug || !uid) {
        setError('חסרים נתונים לסשן סגירת הפערים.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const built = await buildReverseLearningQueue(String(slug), String(uid), targetQuestions);
        if (!cancelled) {
          setQueue(built.queue);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'לא הצלחנו לטעון את סשן סגירת הפערים.');
          setLoading(false);
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [slug, uid, targetQuestions]);

  const current = queue[idx];
  const progress = useMemo(
    () => (queue.length > 0 ? Math.round(((idx + (result ? 1 : 0)) / queue.length) * 100) : 0),
    [idx, queue.length, result],
  );

  const chooseAction = async (action: KpActionRuntime) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      const response = await kpLiveApi.submitScenarioAction({
        scenario_id: current.scenario.id,
        action_id: action.id,
        lesson_id: current.lessonId,
        slug: String(slug),
        user_id: String(uid),
      });

      setResult(response);
      setSessionXp((value) => value + (response.xp_awarded ?? 0));
      setQualityTiers((prev) => [...prev, response.quality_tier]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'לא הצלחנו לשלוח את הבחירה שלך.');
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    setResult(null);
    if (idx + 1 >= queue.length) {
      const summary = summarizeReverseSession({
        answered: qualityTiers.length,
        total: queue.length,
        xpEarned: sessionXp,
        qualityTiers,
      });

      router.replace(buildRuntimeReverseResultPath(runtimeBase, String(slug), runtimeSession, {
        total: String(summary.total),
        answered: String(summary.answered),
        mastery: String(summary.masteryRate),
        improved: String(summary.improvedCount),
        weak: String(summary.weakCount),
        risky: String(summary.riskyCount),
        xp: String(summary.xpEarned),
      }) as any);
      return;
    }
    setIdx((value) => value + 1);
  };

  if (!slug || !uid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={String(slug ?? '')} />;
  }

  if (loading) return <View style={s.center}><ActivityIndicator color={PRIMARY} size="large" /></View>;

  if (error) {
    return (
      <View style={s.center}>
        <View style={s.errorCard}>
          <Text style={s.errorTitle}>הסשן לא נטען</Text>
          <Text style={s.error}>{error}</Text>
          <Pressable style={s.nextBtn} onPress={() => router.replace(buildRuntimeReversePath(runtimeBase, String(slug), runtimeSession) as any)}>
            <Text style={s.nextText}>חזרה למסך החזרה</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (!current) {
    return (
      <View style={s.center}>
        <View style={s.errorCard}>
          <Text style={s.errorTitle}>אין כרגע פערים פעילים לסשן הזה</Text>
          <Text style={s.body}>כדי לבנות מסלול חזרה אמיתי צריך עוד תרגול או סקירת טעויות עדכנית.</Text>
          <Pressable style={s.nextBtn} onPress={() => router.push(buildRuntimePracticePath(runtimeBase, String(slug), runtimeSession) as any)}>
            <Text style={s.nextText}>פתח מרכז תרגול</Text>
          </Pressable>
          <Pressable style={s.altBtn} onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, String(slug), runtimeSession) as any)}>
            <Text style={s.altText}>פתח סקירת טעויות</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={s.root}>
      <View style={s.top}><Text style={s.topTitle}>חזרה ממוקדת · סשן סגירת פערים</Text><Text style={s.topSub}>XP בסשן: {sessionXp}</Text></View>
      <View style={s.progressTrack}><View style={[s.progressFill, { width: `${progress}%` as any }]} /></View>

      <ScrollView contentContainerStyle={s.content}>
        <View style={s.tagRow}><Text style={s.tag}>{current.source === 'mistake' ? 'תיקון טעות אחרונה' : 'חיזוק אזור חלש'}</Text></View>
        <Text style={s.lessonLabel}>{current.lessonTitle}</Text>
        <Text style={s.scenarioTitle}>{current.scenario.title}</Text>
        <Text style={s.body}>{current.scenario.situation}</Text>
        <Text style={s.body}>{current.scenario.decision_point}</Text>

        {!result && current.scenario.actions.map((action) => (
          <Pressable key={action.id} style={s.actionBtn} onPress={() => { void chooseAction(action); }} disabled={submitting}>
            <Text style={s.actionText}>{action.label}</Text>
          </Pressable>
        ))}

        {result && (
          <View style={s.resultCard}>
            <Text style={s.resultTitle}>{QUALITY_LABELS[result.quality_tier] ?? result.quality_tier} · +{result.xp_awarded} XP</Text>
            <Text style={s.body}>{result.consequence?.narrative}</Text>
            <Pressable style={s.nextBtn} onPress={goNext}>
              <Text style={s.nextText}>{idx + 1 >= queue.length ? 'סיים סשן' : 'לתרחיש הבא'}</Text>
            </Pressable>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  top: { paddingTop: 50, paddingHorizontal: 18, paddingBottom: 8 },
  topTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16 },
  topSub: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 12, marginTop: 2 },
  progressTrack: { height: 5, backgroundColor: '#2A274A' },
  progressFill: { height: 5, backgroundColor: PRIMARY },
  content: { padding: 18, gap: 10, paddingBottom: 50 },
  tagRow: { alignItems: 'flex-end' },
  tag: { color: '#22D3EE', fontFamily: theme.fonts.bold, fontSize: 12 },
  lessonLabel: { color: PRIMARY, fontFamily: theme.fonts.bold, textAlign: 'right' },
  scenarioTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 22, textAlign: 'right' },
  body: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22, textAlign: 'right' },
  actionBtn: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 12, padding: 12 },
  actionText: { color: TEXT, fontFamily: theme.fonts.semiBold, textAlign: 'right' },
  resultCard: { marginTop: 8, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  resultTitle: { color: '#34D399', fontFamily: theme.fonts.bold, textAlign: 'right' },
  nextBtn: { backgroundColor: PRIMARY, borderRadius: 10, alignItems: 'center', paddingVertical: 11 },
  nextText: { color: '#fff', fontFamily: theme.fonts.bold },
  errorCard: { width: '100%', maxWidth: 520, backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 10 },
  errorTitle: { color: TEXT, fontFamily: theme.fonts.bold, textAlign: 'right', fontSize: 20 },
  altBtn: { borderRadius: 10, borderWidth: 1, borderColor: BORDER, alignItems: 'center', paddingVertical: 11 },
  altText: { color: TEXT, fontFamily: theme.fonts.bold },
  error: { color: '#FCA5A5', fontFamily: theme.fonts.bold, textAlign: 'center' },
});
